import { Avatar, AvatarSize } from '@eventbrite/eds-avatar';
import {
    CONSUMER_CARD_DEFAULTS,
    DiscoveryEvent,
    EventCard,
    EventClient,
    EventClientProvider,
    transformDiscoveryEvent,
} from '@eventbrite/event-renderer';
import { gettext } from '@eventbrite/i18n';
import React from 'react';
import { AffiliateCodes } from '../../types';
import { ShareProps } from '../ShareEvent/ShareEventRoot';
import './ExpiredEvents.scss';

export type ExpiredEventsPageProps = {
    relatedEvents: DiscoveryEvent[];
    eventName: string;
    organizer: {
        name: string;
        profilePicture?: string;
        url?: string;
    };
    isAuthenticated: boolean;
    shareComponentProps: ShareProps;
};

export const ExpiredEventsPage = ({
    relatedEvents,
    eventName,
    organizer,
    isAuthenticated,
    shareComponentProps,
}: ExpiredEventsPageProps) => {
    return (
        <div className="expired-event js-expired-related-events">
            <div className="expired-wrapper">
                <div className="container">
                    <div>
                        <span className="expired-badge">
                            {gettext('Event ended')}
                        </span>
                    </div>
                    <div className="expired-info">
                        <h3 className="expired-heading">{eventName}</h3>
                        <button className="js-exit-expired-events expired-view-details">
                            {gettext('View details')}
                        </button>
                    </div>
                    <hr className="expired-divider" />
                    <div>
                        <h4 className="expired-organizer-intro">
                            {gettext(
                                'Follow this organizer to stay informed on future events',
                            )}
                        </h4>
                        <div className="expired-organizer">
                            {organizer.profilePicture && (
                                <div>
                                    <Avatar
                                        imageUrl={organizer.profilePicture}
                                        size={AvatarSize.small}
                                    />
                                </div>
                            )}
                            <div>
                                <a
                                    href={organizer.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="expired-event__organizer-link expired-organizer__link"
                                >
                                    <h4>{organizer.name}</h4>
                                    <small className="expired-small">
                                        {gettext('Event creator')}
                                    </small>
                                </a>
                            </div>
                            <div id="listings-root__follow-organizer_expired" />
                        </div>
                    </div>
                </div>
            </div>

            <ExpiredEventsList
                {...{ relatedEvents, isAuthenticated, shareComponentProps }}
            />
        </div>
    );
};

const ExpiredEventsList = ({
    relatedEvents,
    isAuthenticated,
    shareComponentProps,
}: {
    relatedEvents: DiscoveryEvent[];
    isAuthenticated: boolean;
    shareComponentProps: ShareProps;
}): JSX.Element => {
    const affCode = AffiliateCodes.AFF_RELATED_EVENTS_EXPIRED;
    const eventClient = new EventClient();

    return (
        <div className="expired-wrapper expired-wrapper--bg-gray">
            <div className="container">
                <div>
                    <p className="related-events__heading">
                        {gettext('Events you might like')}
                    </p>
                </div>
                <ul className="expired-events-grid">
                    <EventClientProvider client={eventClient}>
                        {relatedEvents.map((event: any, i: number) => {
                            const shareEventProps = {
                                eventId: event.id,
                                eventName: event.name,
                                eventUrl: event.url,
                                ...shareComponentProps,
                            };
                            return (
                                <li
                                    key={i}
                                    className="expired-events-grid__item"
                                >
                                    <EventCard
                                        {...CONSUMER_CARD_DEFAULTS}
                                        eventData={transformDiscoveryEvent(
                                            event,
                                        )}
                                        affCode={affCode}
                                        key={event.id}
                                        gaCategory="expired-events"
                                        isAuthenticated={isAuthenticated}
                                        shareOptions={shareEventProps}
                                    />
                                </li>
                            );
                        })}
                    </EventClientProvider>
                </ul>
            </div>
        </div>
    );
};
